import React, { useState } from 'react';
import { Check, Zap, Star, Award, Trophy } from 'lucide-react';
import api, { API_BASE_URL } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const PricingTier = ({ tier }) => {
  const { isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  // Removed unused isHovered state since we're using CSS hover states

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      // Don't hard-stop at the buy moment. Remember the chosen plan and send the
      // user through Google login; AuthContext resumes checkout automatically once
      // they're authenticated.
      try {
        sessionStorage.setItem('pendingCheckoutVariant', tier.variantName);
      } catch (_) { /* sessionStorage unavailable — proceed to login anyway */ }
      setIsLoading(true);
      window.location.href = `${API_BASE_URL}/auth/google`;
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post('/api/payments/create-checkout', {
        variantName: tier.variantName,
      });
      const { checkoutUrl } = response.data;
      // Redirect to Lemon Squeezy
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      alert('Could not initiate checkout. Please try again.');
      setIsLoading(false);
    }
  };

  const getTierIcon = () => {
    switch(tier.name) {
      case 'One-Off':
        return <Award className="h-6 w-6 text-amber-500" />;
      case 'Starter':
        return <Zap className="h-6 w-6 text-blue-500" />;
      case 'Pro':
        return <Star className="h-6 w-6 text-yellow-400" />;
      case 'All-Star':
        return <Trophy className="h-6 w-6 text-purple-500" />;
      default:
        return <Check className="h-6 w-6 text-green-500" />;
    }
  };

  return (
    <div
      className={`relative rounded-2xl p-8 flex flex-col transition-all duration-300 transform hover:scale-[1.02] ${
        tier.featured 
          ? 'bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white border-2 border-yellow-400 shadow-2xl hover:shadow-yellow-500/20' 
          : 'bg-white border-2 border-gray-100 hover:border-blue-100 hover:shadow-xl'
      }`}
      // Using CSS hover states instead of React state
    >
      {tier.featured && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className="bg-yellow-400 text-gray-900 text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
            MOST POPULAR
          </div>
        </div>
      )}
      
      <div className="flex items-center mb-6">
        <div className={`p-2 rounded-lg mr-4 ${
          tier.featured ? 'bg-yellow-400/20' : 'bg-blue-50'
        }`}>
          {getTierIcon()}
        </div>
        <h3 className={`text-2xl font-bold ${
          tier.featured ? 'text-white' : 'text-gray-900'
        }`}>
          {tier.name}
        </h3>
      </div>
      
      <div className="mb-6">
        <p className={`text-4xl font-extrabold ${tier.featured ? 'text-white' : 'text-gray-900'}`}>
          {tier.price}
          {tier.period && (
            <span className={`text-lg font-medium ${tier.featured ? 'text-gray-300' : 'text-gray-600'} ml-1`}>
              {tier.period}
            </span>
          )}
        </p>
        <p className={`mt-2 text-sm ${tier.featured ? 'text-gray-300' : 'text-gray-500'}`}>
          {tier.description}
        </p>
      </div>
      
      <ul className="space-y-3 mb-8 flex-grow">
        {tier.features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <Check className={`flex-shrink-0 h-5 w-5 mt-0.5 ${
              tier.featured ? 'text-green-400' : 'text-green-500'
            }`} />
            <span className={`ml-3 ${tier.featured ? 'text-gray-200' : 'text-gray-600'}`}>
              {feature}
            </span>
          </li>
        ))}
      </ul>
      
      <div className="mt-auto">
        <button
          type="button"
          onClick={handleCheckout}
          disabled={isLoading}
          className={`w-full py-4 px-6 rounded-xl font-bold text-center transition-all duration-300 ${
            tier.featured
              ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-gray-900 hover:shadow-lg hover:shadow-yellow-400/30 hover:-translate-y-0.5'
              : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:shadow-lg hover:shadow-blue-500/30 hover:-translate-y-0.5'
          } disabled:opacity-75 disabled:cursor-not-allowed`}
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          ) : (
            <span className="flex items-center justify-center">
              {tier.buttonText}
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
              </svg>
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

const PricingSection = ({ GetStartedButton }) => {
  const tiers = [
    {
      name: 'Season Pass',
      variantName: 'pro',
      price: '$19.99',
      period: '/mo',
      description: 'Never miss a game-worthy memory all season long.',
      features: [
        '10 Recaps/Month',
        'Best value for active families',
        'Great for tournament seasons',
        'Cancel anytime',
      ],
      buttonText: 'Get Season Pass',
      featured: true,
    },
    {
      name: 'Game Day',
      variantName: 'one_off',
      price: '$5.99',
      period: '/game',
      description: 'Try it for one game — championship, tournament, or just a moment worth keeping.',
      features: [
        '1 Full Podcast Recap',
        'Real player shout-outs',
        'Shareable link for family & friends',
        'No subscription needed',
      ],
      buttonText: 'Try It Now',
    },
    {
      name: 'Weekly Fan',
      variantName: 'starter',
      price: '$9.99',
      period: '/mo',
      description: 'For the parent who wants to capture the season, game by game.',
      features: [
        '4 Recaps/Month',
        'Great for weekly league play',
        'Share with family every week',
        'Pause or cancel anytime',
      ],
      buttonText: 'Start Weekly Plan',
    },
    {
      name: 'League Plan',
      variantName: 'all_star',
      price: '$39.99',
      period: '/mo',
      description: 'For coaches and team admins who want every family to feel the buzz.',
      features: [
        '25 Recaps/Month',
        'Cover every game all season',
        'Keep your whole team energized',
        'Perfect for travel & tournament teams',
      ],
      buttonText: 'Team Sign Up',
    },
  ];

  return (
    <section id="pricing" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section header */}
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-bold text-royal-blue uppercase tracking-widest mb-3">Pricing</span>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight">
            Pick Your Plan, <span className="text-yellow-500">Start Celebrating</span>
          </h2>
          <p className="text-gray-500 mt-3 max-w-xl mx-auto text-base">
            One big game or a whole season — there's a plan that fits. No hidden fees, cancel anytime.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiers.map((tier) => (
            <PricingTier key={tier.name} tier={tier} GetStartedButton={GetStartedButton} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
